import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Users, Bus, Star, Heart } from "lucide-react";
import { STATS } from "@/data/content";

const ICONS = { Users, Bus, Star, Heart };

function Counter({ value, suffix }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const target = parseInt(value, 10);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          let start = 0;
          const dur = 1400;
          const t0 = performance.now();
          const step = (t) => {
            const p = Math.min((t - t0) / dur, 1);
            setN(Math.floor(p * target));
            if (p < 1) requestAnimationFrame(step);
            else setN(target);
          };
          requestAnimationFrame(step);
          obs.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);

  return (
    <span ref={ref} className="font-bebas text-5xl md:text-6xl text-gold leading-none">
      {n}
      {suffix}
    </span>
  );
}

export default function Stats() {
  return (
    <section data-testid="stats-section" className="relative bg-[#0B0E14] py-16 md:py-20 border-y border-[#1E2638]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {STATS.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <motion.div
                key={s.label}
                data-testid={`stat-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="text-center rounded-2xl border border-[#1E2638] bg-[#131822] p-6 hover:border-gold/40 transition-colors"
              >
                <Icon className="mx-auto mb-3 text-primary" size={28} />
                <Counter value={s.value} suffix={s.suffix} />
                <p className="mt-2 text-xs md:text-sm font-semibold uppercase tracking-widest text-white/60">{s.label}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
